import { useCallback, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { SMLAnalytics } from '../../analytics/sml/analytics';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';
import { useTRPCAPI, useTRPCClientAPI } from '../../../trpc/trpc';

export interface ChecklistVisibility {
  /** Hides the card on Home and the sidebar pill. Settings -> Setup keeps the way back. */
  dismiss: () => void;
  /** Shows the checklist on Home again. */
  restore: () => void;
}

/**
 * Dismiss and restore of the checklist of one artist (design: onboarding d1, d4, d6; one checklist
 * per artist). `accountId` is `state.checklistArtist.accountId`. Dismiss patches every cached answer
 * of that artist at once, so the card goes on the click. Both read the answers again after the call.
 */
export function useChecklistVisibility(accountId: number | null): ChecklistVisibility {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();

  const reload = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: trpcAPI.onboarding.state.queryKey() }).catch(console.error);
  }, [queryClient, trpcAPI]);

  const input = useMemo(() => (accountId === null ? {} : { accountId }), [accountId]);

  const dismiss = useCallback(() => {
    queryClient.setQueriesData(
      { queryKey: trpcAPI.onboarding.state.queryKey() },
      (old: OnboardingStateAnswer | undefined) =>
        old === undefined || old.checklistArtist?.accountId !== accountId
          ? old
          : { ...old, checklist: null, checklistDismissed: true }
    );
    if (accountId !== null) {
      SMLAnalytics.track('onboarding_checklist_dismissed', { accountId: `${accountId}` }).catch(console.error);
    }
    trpcClient.onboarding.dismissChecklist.mutate(input).then(reload).catch(console.error);
  }, [accountId, input, queryClient, reload, trpcAPI, trpcClient]);

  const restore = useCallback(() => {
    trpcClient.onboarding.restoreChecklist.mutate(input).then(reload).catch(console.error);
  }, [input, reload, trpcClient]);

  return useMemo(() => ({ dismiss, restore }), [dismiss, restore]);
}
